import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import MaldivesDirectSearchFlightCalendarMobile from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendarMobile'
import CharterBundleCalendarActions from './CharterBundleCalendarActions'
import { useOfferCharterCalendarData } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useOfferCharterCalendarData'
import { filterDateRangesByNights } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/utils'
import useCharterDateRangesByMonth from 'hooks/CharterFlights/useCharterDateRangesByMonth'
import { parseAsBoolean, useQueryState } from 'nuqs'
import type { OfferDurationOption } from '../DurationTabs/OfferDurationTabs'
import type { OfferPagePriceMode } from 'contexts/OfferPage/offerPageStateReducer'
import type { FlightsClassTypes } from 'constants/flight'
import { CHARTER_FLIGHT_DETAILS } from 'constants/charterFlight'
import moment from 'moment'
import { getCharterFareClassPolicy } from 'lib/charterFlights/charterOfferFareClassRestrictions'

export interface CharterTripOption {
  checkIn: string
  checkOut: string
  departDate: string
  returnDate: string
}

interface Props {
  offer: App.HotelOffer
  occupancies: Array<App.Occupants>
  duration: number
  durationOptions: Array<OfferDurationOption>
  priceMode: OfferPagePriceMode
  fareClass: FlightsClassTypes
  charterBundleRoute: App.CharterBundleRoute
  checkInDate?: string
  onDurationChange: (nights: number) => void
  onClose?: () => void
  onBack?: () => void
  onDateSelected: (option: CharterTripOption) => void
  onContinue?: () => void
  onResetDates?: () => void
  onFareClassChange: (fareClass: FlightsClassTypes) => void
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>
  disableContinue?: boolean
}

function CharterCalendarFilter(props: Props) {
  const intl = useIntl()
  const {
    offer,
    occupancies,
    duration,
    durationOptions,
    priceMode,
    fareClass,
    charterBundleRoute,
    checkInDate,
    onDurationChange,
    onClose,
    onBack,
    onDateSelected,
    onContinue,
    onResetDates,
    onFareClassChange,
    scrollContainerRef,
    disableContinue,
  } = props

  const [luxPlusEnabled, setLuxPlusEnabled] = useQueryState('luxPlus', parseAsBoolean.withDefault(true))
  const [selectedMonth, setSelectedMonth] = useState(checkInDate ? moment(checkInDate).format('MMM') : moment().format('MMM'))

  const { dateRangesByMonth: allDateRangesByMonth } = useCharterDateRangesByMonth({
    charterBundleRoute,
    occupancies,
    fareClass,
    disabled: occupancies.length === 0,
  })

  const dateRangesByMonth = useMemo(
    () => filterDateRangesByNights(allDateRangesByMonth, duration),
    [allDateRangesByMonth, duration],
  )

  const flightCalendarData = useOfferCharterCalendarData({
    offer,
    nights: duration,
    rooms: occupancies,
    fareClass,
    dateRangesByMonth,
    charterBundleRoute,
  })

  const selectedOptionId = useMemo(() => checkInDate ? `${checkInDate}-${duration}n` : undefined, [checkInDate, duration])

  const durationDropdownOptions = useMemo(() => {
    // Get valid durations for charter flights (e.g., 5 and 12 nights for Maldives)
    const validCharterDurations = CHARTER_FLIGHT_DETAILS[charterBundleRoute]?.validDurations ?? []
    // Only include duration options that are valid charter durations and have available (non-sold-out) packages
    return durationOptions
      .filter(option => !option.soldOut && validCharterDurations.includes(option.duration))
      .map(option => ({
        value: option.duration,
        label: intl.formatMessage(
          {
            id: 'JiMJ6iF78M',
            defaultMessage: '{count, plural, one {# night} other {# nights}} & flights',
            description: 'Duration option label showing night count with flights for charter calendar',
          },
          { count: option.duration },
        ),
      }))
  }, [intl, durationOptions, charterBundleRoute])
  const charterFareClassPolicy = useMemo(() => {
    return getCharterFareClassPolicy({
      campaignTags: offer.campaignTags,
      occupancies,
    })
  }, [offer.campaignTags, occupancies])

  const actionsElement = (
    <CharterBundleCalendarActions
      nights={duration}
      onDurationChange={onDurationChange}
      durationOptions={durationDropdownOptions}
      luxPlusEnabled={luxPlusEnabled}
      onLuxPlusToggle={setLuxPlusEnabled}
      fareClass={fareClass}
      onFareClassChange={onFareClassChange}
      availableFareClasses={charterFareClassPolicy.allowedFareClasses}
    />
  )

  const handleDateSelect = (option: CharterTripOption) => {
    onDateSelected(option)
    setSelectedMonth(moment(option.checkIn).format('MMM'))
  }

  const secondaryActionProps = onResetDates ? {
    children: intl.formatMessage({
      id: 'AVgf6troQ3',
      defaultMessage: 'Reset',
      description: 'Button to reset selected dates in charter calendar filter',
    }),
    onClick: onResetDates,
    type: 'button' as const,
  } : undefined

  const primaryActionProps = onContinue ? {
    children: intl.formatMessage({
      id: '2xvN9YEDXg',
      defaultMessage: 'Continue',
      description: 'Button to continue after selecting dates in charter calendar filter',
    }),
    onClick: onContinue,
    type: 'button' as const,
    disabled: disableContinue,
  } : undefined

  return (
    <>
      <ModalHeader
        title={intl.formatMessage({
          id: 'd4FPk4/HrN',
          defaultMessage: 'Select your trip dates',
          description: 'Modal header title for charter flight calendar date selection',
        })}
        onCloseButtonClick={onClose}
        onBackButtonClick={onBack}
      >
        <HorizontalContainerWithFadedOverflow overflowGutter={20} gap={8}>
          {actionsElement}
        </HorizontalContainerWithFadedOverflow>
      </ModalHeader>
      <ModalBody ref={scrollContainerRef}>
        <ModalContent>
          <MaldivesDirectSearchFlightCalendarMobile
            onDateSelect={handleDateSelect}
            nights={duration}
            occupancies={occupancies}
            luxPlusEnabled={luxPlusEnabled}
            itemBundleDataByDate={flightCalendarData.itemBundleDataByDate}
            dateRangesByMonth={dateRangesByMonth}
            selectedMonth={selectedMonth}
            onMonthChange={setSelectedMonth}
            fareClass={fareClass}
            priceMode={priceMode}
            selectedOptionId={selectedOptionId}
            mode="offer"
            offer={offer}
            scrollContainerRef={scrollContainerRef}
          />
        </ModalContent>
      </ModalBody>
      <ModalFooter
        secondaryActionProps={secondaryActionProps}
        primaryActionProps={primaryActionProps}
      />
    </>
  )
}

export default CharterCalendarFilter
